import { memo, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Flex,
  Heading,
  SearchInput,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import {
  IconAgent,
  IconArrowsRotate,
  IconCalendar,
  IconClock,
  IconCodeBranch,
  IconEnvelope,
  IconGripVertical,
  IconLinkHorizontal,
  IconMessages,
  IconPen,
  IconPlus,
  IconSearch,
  IconXmark,
} from '@optimizely/axiom-icons';

import { useGetSpecializedAgentsList } from '@frontend/features/agents/hooks/useSpecializedAgents';
import {
  MatchType,
  SpecializedAgentStep,
  Step,
  StepType,
  Trigger,
  TriggerType,
} from '@frontend/types/workflow-agent';

import * as styles from './WorkflowLeftSidebar.css';

type ConditionItem = {
  id: string;
  type: 'condition';
  name: string;
  description: string;
  matching_condition: string;
  match_type: MatchType;
};

type ForLoopItem = {
  id: string;
  type: 'for_loop';
  name: string;
  description: string;
};

type SidebarItem = Step | Trigger | ConditionItem | ForLoopItem;

type SidebarSection = {
  id: string;
  label: string;
  items: SidebarItem[];
  draggable?: boolean;
  defaultOpen?: boolean;
};

const getIconComponent = (iconName: string): React.ComponentType => {
  const iconMap: { [key: string]: React.ComponentType } = {
    pen: IconPen,
    messages: IconMessages,
    clock: IconClock,
    search: IconSearch,
    grip: IconGripVertical,
    specialized_agent: IconAgent,
    webhook: IconLinkHorizontal,
    scheduled: IconCalendar,
    message: IconMessages,
    email: IconEnvelope,
    condition: IconCodeBranch,
    for_loop: IconArrowsRotate,
  };
  return iconMap[iconName] || IconPen;
};

const getSidebarItemDisplayData = (item: SidebarItem) => {
  if (item.type === StepType.SPECIALIZED) {
    return {
      id: item.step_id,
      label: item.name,
      icon: 'specialized_agent',
    };
  } else if (
    'type' in item &&
    (item.type === TriggerType.WEBHOOK ||
      item.type === TriggerType.SCHEDULED ||
      item.type === TriggerType.MESSAGE ||
      item.type === TriggerType.EMAIL)
  ) {
    return {
      id: item.id,
      label: item.name,
      icon:
        item.type === TriggerType.WEBHOOK
          ? 'webhook'
          : item.type === TriggerType.SCHEDULED
            ? 'scheduled'
            : item.type === TriggerType.MESSAGE
              ? 'message'
              : 'email',
    };
  } else if (item.type === 'condition') {
    return {
      id: item.id,
      label: item.name,
      icon: 'condition',
    };
  } else if (item.type === 'for_loop') {
    return {
      id: 'id' in item ? item.id : item.step_id,
      label: item.name,
      icon: 'for_loop',
    };
  }

  return {
    id: 'step_id' in item ? item.step_id : 'id' in item ? (item as { id: string }).id : 'unknown',
    label: item.name,
    icon: 'specialized_agent',
  };
};

const triggers: Trigger[] = [
  {
    id: 'trigger-chat-input',
    type: TriggerType.MESSAGE,
    name: 'Chat Input',
    description: 'Trigger when user sends a message',
    is_active: true,
  },
  {
    id: 'trigger-scheduler',
    type: TriggerType.SCHEDULED,
    name: 'Scheduler',
    description: 'Trigger on a schedule',
    schedule_type: 'one_time',
    workflow_agent_id: '',
    version: 1,
    is_active: true,
    created_at: new Date().toISOString(),
    created_by: '',
    updated_at: new Date().toISOString(),
    updated_by: '',
  },
  {
    id: 'trigger-webhook',
    type: TriggerType.WEBHOOK,
    name: 'Webhook',
    description: 'Trigger via webhook',
    payload_content_type: 'application/json',
    is_active: true,
  },
  {
    id: 'trigger-email',
    type: TriggerType.EMAIL,
    name: 'Email',
    description: 'Trigger via email',
    workflow_agent_id: '',
    recipient: '',
    email_address: '',
    ses_registered: false,
    is_active: true,
    version: 1,
    invocation_count: 0,
    last_invocation: null,
    created_at: new Date().toISOString(),
    created_by: '',
    updated_at: new Date().toISOString(),
    updated_by: '',
    emails_received_count: 0,
    last_email_received: null,
  },
];

const logicItems: Array<ConditionItem | ForLoopItem> = [
  {
    id: 'condition-dummy',
    type: 'condition',
    name: 'Condition',
    description: 'Add conditional logic to workflow',
    matching_condition: '',
    match_type: MatchType.EQUALS,
  },
  {
    id: 'for-loop-dummy',
    type: 'for_loop',
    name: 'Loop',
    description: 'Execute steps for each item in a collection',
  },
];

type WorkFlowLeftSidebarProps = {
  hasUnsavedAsyncNode?: boolean;
  onOpenSACreation?: () => void;
};

function WorkFlowLeftSidebar({
  hasUnsavedAsyncNode = false,
  onOpenSACreation,
}: WorkFlowLeftSidebarProps) {
  const [searchValue, setSearchValue] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [draggedItem, setDraggedItem] = useState<SidebarItem | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { agents, isPending: isAgentsLoading } = useGetSpecializedAgentsList();

  const handleDragStart = (e: React.DragEvent, item: SidebarItem) => {
    setDraggedItem(item);
    setIsDragging(true);

    const dragData = {
      type: 'sidebar-item',
      nodeData: item,
    };

    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const sidebarData: SidebarSection[] = useMemo(() => {
    const agentItems: SpecializedAgentStep[] = agents.map(agent => ({
      type: StepType.SPECIALIZED as const,
      agent_id: agent.id,
      name: agent.name,
      description: agent.description,
      step_id: agent.id,
      is_system_agent: agent.is_system_agent,
      links: agent.links,
      is_installed: agent.is_installed,
    }));

    const triggerItems: Trigger[] = triggers.map(trigger => ({
      ...trigger,
    }));

    // Dragging is only disabled when there's an unsaved async node
    const canDrag = !hasUnsavedAsyncNode;

    return [
      {
        id: 'triggers',
        label: 'Triggers',
        items: triggerItems,
        draggable: canDrag,
        defaultOpen: true,
      },
      {
        id: 'logic',
        label: 'Logic',
        items: logicItems,
        draggable: canDrag,
        defaultOpen: true,
      },
      {
        id: 'agents',
        label: 'Agents',
        items: agentItems,
        draggable: canDrag,
        defaultOpen: true,
      },
    ];
  }, [agents, hasUnsavedAsyncNode]);

  const filteredSections = sidebarData
    .map(section => {
      if (!searchValue) return section;

      const sectionMatches = section.label.toLowerCase().includes(searchValue.toLowerCase());

      const filteredItems = section.items.filter(item => {
        const { label } = getSidebarItemDisplayData(item);
        return label.toLowerCase().includes(searchValue.toLowerCase());
      });

      if (sectionMatches || filteredItems.length > 0) {
        return {
          ...section,
          items: sectionMatches ? section.items : filteredItems,
        };
      }

      return null;
    })
    .filter((section): section is SidebarSection => section !== null);

  return (
    <Flex className={styles.visualBuilderContainer} flexDirection="column">
      <Flex className={styles.sidebarHeader}>
        <Flex className={styles.headerFlex}>
          {!isSearchOpen && (
            <Heading level="4" color="fg.default">
              Components
            </Heading>
          )}
          {isSearchOpen ? (
            <Box className={styles.searchContainer}>
              <SearchInput
                placeholder="Search components..."
                value={searchValue}
                style={{ height: '40px', width: '100%' }}
                onChange={e => setSearchValue(e.target.value)}
                addonAfter={
                  <Button
                    size="sm"
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchValue('');
                    }}
                    appearance="subtle"
                    aria-label="Close search"
                  >
                    <IconXmark />
                  </Button>
                }
                autoFocus
              />
            </Box>
          ) : (
            <Button
              appearance="subtle"
              size="sm"
              onClick={() => setIsSearchOpen(true)}
              aria-label="Open search"
            >
              <IconSearch />
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex className={styles.sidebarContent}>
        <Flex className={styles.sectionsContainer}>
          {filteredSections.map(section => (
            <Box key={section.id} style={{ paddingRight: '8px' }}>
              <Disclosure defaultOpen={section.defaultOpen}>
                <DisclosureTrigger
                  chevronPosition="end"
                  className={
                    section.defaultOpen ? styles.sectionTriggerOpen : styles.sectionTrigger
                  }
                >
                  <Flex className={styles.sectionTriggerFlex}>
                    <Text
                      className={
                        section.defaultOpen ? styles.sectionTextStyleOpen : styles.sectionTextStyle
                      }
                      lineClamp="1"
                    >
                      {section.label}
                      {section.id === 'agents' && isAgentsLoading && ' (Loading...)'}
                    </Text>
                    {section.id === 'agents' && onOpenSACreation && (
                      <Tooltip content="Create new specialized agent">
                        <Button
                          appearance="subtle"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation(); // Prevent disclosure toggle
                            onOpenSACreation();
                          }}
                          aria-label="Create new specialized agent"
                        >
                          <IconPlus />
                        </Button>
                      </Tooltip>
                    )}
                  </Flex>
                </DisclosureTrigger>

                <DisclosureContent>
                  <Box className={styles.sectionItemsContainer}>
                    {section.items.map(item => {
                      const { id, label, icon } = getSidebarItemDisplayData(item);
                      const IconComponent = getIconComponent(icon);
                      const isDisabled = !section.draggable;

                      const flexElement = (
                        <Flex
                          key={id}
                          className={`${
                            section.draggable
                              ? styles.sectionItemDraggable
                              : styles.sectionItemDisabled
                          } ${
                            section.draggable && isDragging && draggedItem === item
                              ? styles.draggedItem
                              : ''
                          }`}
                          draggable={section.draggable}
                          onDragStart={e => section.draggable && handleDragStart(e, item)}
                          onDragEnd={handleDragEnd}
                          onDragOver={handleDragOver}
                        >
                          <IconComponent />
                          <Text lineClamp="1" className={styles.itemTextStyle}>
                            {label}
                          </Text>
                          {section.draggable && (
                            <IconGripVertical className={styles.dragHandleStyle} />
                          )}
                        </Flex>
                      );

                      if (isDisabled) {
                        const tooltipContent =
                          'Complete configuring and save the current node before adding new components';

                        return (
                          <Tooltip key={id} content={tooltipContent}>
                            {flexElement}
                          </Tooltip>
                        );
                      }

                      // Add tooltip for agents showing full name
                      if (section.id === 'agents') {
                        return (
                          <Tooltip key={id} content={item.name}>
                            {flexElement}
                          </Tooltip>
                        );
                      }

                      return flexElement;
                    })}
                  </Box>
                </DisclosureContent>
              </Disclosure>
            </Box>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
}

export default memo(WorkFlowLeftSidebar);
